import Button from "@/components/Button"
import Dialog from "@/components/Dialog"
import InputErrorMessage from "@/components/InputErrorMessage"
import { SelectInput, TextInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Spinner from "@/components/Spinner"
import Toast from "@/components/Toast"
import { useCheckPatientMRNMutation } from "@/data/queries"
import {
  ClinicalProtocolType,
  clinicalProtocolTypes,
} from "@/model/ClinicalProtocol"
import { Organization } from "@/model/Organization"
import { buildPath, ROUTES, withQuery } from "@/routes"
import { zodResolver } from "@hookform/resolvers/zod"
import { orderBy } from "lodash"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import { z } from "zod"

type AddPatientFormData = {
  medicalRecordNumber: string
  organizationId: string
  clinicalProtocolType: ClinicalProtocolType
}

type AddPatientDialogProps = {
  organizations: Organization[]
  isOpen: boolean
  onClose: () => void
}

const formSchema = z.object({
  medicalRecordNumber: z.string().trim().nonempty("MRN is a required field"),
  organizationId: z.string().nonempty("Please select an organization"),
  clinicalProtocolType: z.enum(
    clinicalProtocolTypes,
    "Please select a prescription type"
  ),
})

const AddPatientDialog = ({
  organizations,
  isOpen,
  onClose,
}: AddPatientDialogProps) => {
  const navigate = useNavigate()
  const { register, handleSubmit, formState, reset } =
    useForm<AddPatientFormData>({
      mode: "onChange",
      resolver: zodResolver(formSchema),
    })
  const { errors } = formState

  const {
    mutateAsync: checkPatientMRN,
    isPending: isCheckingPatientMRN,
    isError: isErrorCheckPatientMRN,
  } = useCheckPatientMRNMutation()

  const organizationOptions = [
    {
      title: "-- Select an organization --",
      value: "",
      disabled: true,
    },
    ...orderBy(
      organizations.map((org) => ({
        title: org.shortName,
        tooltip: org.longName,
        value: org.id,
      })),
      ({ title }) => title.toLowerCase()
    ),
  ]

  const clinicalProtocolOptions = [
    {
      title: "-- Select a prescription type --",
      value: "",
      disabled: true,
    },
    {
      title: "Diabetes",
      value: "diabetes",
    },
    {
      title: "Hypertension",
      value: "hypertension",
    },
  ]

  const close = () => {
    onClose()
    reset()
  }

  const onSubmit = async (formData: AddPatientFormData) => {
    try {
      const patient = await checkPatientMRN({
        organizationId: formData.organizationId,
        medicalRecordNumber: formData.medicalRecordNumber.trim(),
      })

      if (patient) {
        navigate(
          buildPath(ROUTES.POPULATION.PATIENT_DETAIL, {
            bloomlifeId: patient.bloomlifeId,
          })
        )
        toast.custom((toastId) => (
          <Toast
            description="The entered MRN belongs to this patient."
            toastId={toastId}
          />
        ))
        return
      }

      // If patient is undefined or null, always route to add-new-patient
      navigate(
        withQuery(ROUTES.POPULATION.ADD_PATIENT, {
          mrn: formData.medicalRecordNumber,
          organizationId: formData.organizationId,
          clinicalProtocolType: formData.clinicalProtocolType,
        })
      )
    } catch {
      toast.custom((toastId) => (
        <Toast
          description="Something went wrong while checking the MRN."
          toastId={toastId}
          variant="error"
        />
      ))
    }
  }

  return (
    <Dialog
      className="max-w-lg"
      title="Enter MRN"
      isOpen={isOpen}
      onClose={close}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-3"
        autoComplete="off"
      >
        <div className="flex flex-col gap-1">
          <InputLabel htmlFor="mrn" className="ml-1">
            MRN
          </InputLabel>
          <TextInput
            id="mrn"
            placeholder="Enter MRN"
            isError={!!errors?.medicalRecordNumber?.message}
            {...register("medicalRecordNumber")}
          />
          <InputErrorMessage
            message={errors?.medicalRecordNumber?.message}
            className="ml-1"
          />
        </div>

        <div className="flex flex-col gap-1">
          <InputLabel htmlFor="organization" className="ml-1">
            Organization
          </InputLabel>
          <SelectInput
            id="organization"
            options={organizationOptions}
            defaultValue=""
            {...register("organizationId")}
          />
          <InputErrorMessage
            message={errors?.organizationId?.message}
            className="ml-1"
          />
        </div>

        <div className="flex flex-col gap-1">
          <InputLabel htmlFor="prescription-type" className="ml-1">
            Prescription type
          </InputLabel>
          <SelectInput
            id="prescription-type"
            options={clinicalProtocolOptions}
            defaultValue=""
            {...register("clinicalProtocolType")}
          />
          <InputErrorMessage
            message={errors?.clinicalProtocolType?.message}
            className="ml-1"
          />
        </div>

        {isErrorCheckPatientMRN && (
          <p className="italic text-red-600">
            Something went wrong while checking the MRN
          </p>
        )}

        <div className="mt-4 flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="neutral"
            onClick={close}
            disabled={isCheckingPatientMRN}
          >
            Cancel
          </Button>
          <Button
            className="flex items-center gap-3"
            type="submit"
            variant="primary"
            disabled={isCheckingPatientMRN}
          >
            {isCheckingPatientMRN && <Spinner className="h-4 w-4" />}
            Check MRN
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

export default AddPatientDialog
